import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Eagerly loaded: the two most-trafficked pages. Keeping these in the
// initial bundle keeps first-paint navigation snappy for the common
// path (homepage → click parcel → detail page).
import SearchPage from './pages/SearchPage';
import ParcelDetail from './pages/ParcelDetail';

// Wrap a dynamic import() so a transient chunk-load failure (a network
// blip, or a chunk that briefly 404s during a deploy asset-swap) retries
// once before giving up. On a persistent failure the rejection propagates
// to the top-level <ErrorBoundary>, which renders a recovery UI with a
// manual "Reload page" action — we deliberately do NOT auto-reload, so a
// chunk that cannot be fetched can never spin into an infinite reload loop
// (regression #953: the lazy /login chunk 404'd under the /land-ai
// sub-path and the auth redirect re-fired endlessly).
function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch(
      () =>
        new Promise((resolve, reject) => {
          setTimeout(() => factory().then(resolve, reject), 350);
        }),
    ),
  );
}

// Lazy-loaded: pages a typical user reaches infrequently or only after
// a deliberate action. React.lazy + the webpack chunk-name comment
// produces a separately-fetched JS chunk per route, trimming the initial
// bundle (currently ~5 MB unminified) and deferring the cost until the
// user actually navigates there.
const SavedParcels         = lazyWithRetry(() => import(/* webpackChunkName: "saved" */ './pages/SavedParcels'));
const AdminPanel           = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/AdminPanel'));
const ModelEvalDashboard   = lazyWithRetry(() => import(/* webpackChunkName: "admin-model-eval" */ './pages/ModelEvalDashboard'));
const NotificationSettings = lazyWithRetry(() => import(/* webpackChunkName: "notifications" */ './pages/NotificationSettings'));
const Watchlists           = lazyWithRetry(() => import(/* webpackChunkName: "watchlists" */ './pages/Watchlists'));
const Digest               = lazyWithRetry(() => import(/* webpackChunkName: "digest" */ './pages/Digest'));
const Opportunities        = lazyWithRetry(() => import(/* webpackChunkName: "opportunities" */ './pages/Opportunities'));
const Properties           = lazyWithRetry(() => import(/* webpackChunkName: "properties" */ './pages/Properties'));
const BigCheapAcreage      = lazyWithRetry(() => import(/* webpackChunkName: "screens" */ './pages/BigCheapAcreage'));
const StaleAcreage         = lazyWithRetry(() => import(/* webpackChunkName: "stale-acreage" */ './pages/StaleAcreage'));
const MineralAdjacentScreen = lazyWithRetry(() => import(/* webpackChunkName: "mineral-adjacent" */ './pages/MineralAdjacentScreen'));
const BuildableRetreatScreen = lazyWithRetry(() => import(/* webpackChunkName: "buildable-retreat" */ './pages/BuildableRetreatScreen'));
const Compare              = lazyWithRetry(() => import(/* webpackChunkName: "compare" */ './pages/Compare'));
const ParcelResearch       = lazyWithRetry(() => import(/* webpackChunkName: "research" */ './pages/ParcelResearch'));
const ResearchLibrary      = lazyWithRetry(() => import(/* webpackChunkName: "research" */ './pages/ResearchLibrary'));
const Dashboard            = lazyWithRetry(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'));
const Report               = lazyWithRetry(() => import(/* webpackChunkName: "report" */ './pages/Report'));
const ParcelPrint          = lazyWithRetry(() => import(/* webpackChunkName: "print" */ './pages/ParcelPrint'));
const Login                = lazyWithRetry(() => import(/* webpackChunkName: "auth" */ './pages/Login'));
const Register             = lazyWithRetry(() => import(/* webpackChunkName: "auth" */ './pages/Register'));

import { useNavigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import RequireAuth from './components/RequireAuth';
import Navbar from './components/Navbar';
import ComparePinBar from './components/ComparePinBar';
import BottomTabBar from './components/BottomTabBar';
import AppSidebar from './components/AppSidebar';
import MoreSheet from './components/MoreSheet';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ScrollRestoration from './hooks/useScrollRestoration';
import useBreakpoint from './hooks/useBreakpoint';
import useAlertsWebSocket from './hooks/useAlertsWebSocket';
import { listBookmarks } from './services/api';

// Handles the 'auth:unauthorized' event dispatched by api.js when a
// 401 comes back. Lives inside the Router so navigate() works without
// a full page reload — fixes the "logged out on navigation" bug (#173).
function AuthRedirectHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      const returnPath = e.detail?.returnPath || '';
      navigate(`/login${returnPath ? `?return=${encodeURIComponent(returnPath)}` : ''}`);
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [navigate]);
  return null;
}

// Tiny fallback while a lazy-loaded route's chunk downloads. Kept
// minimal so it doesn't flash for fast networks.
function RouteFallback() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
      Loading…
    </div>
  );
}

// MobileShell — owns the phone+tablet primary nav surfaces (BottomTabBar /
// AppSidebar / MoreSheet) and the badge sources they share. Rendered
// inside AuthProvider so it can read `useAuth()` for the alerts
// websocket auth key and the MoreSheet's logout action.
//
// Rendered as a sibling of <main>, NOT a wrapper, so the existing
// route tree is unaffected on desktop. Returns null on desktop to
// avoid mounting the WS / bookmark fetch when nobody can see them.
//
// PR-5 (#704) extends MobileShell to render AppSidebar at tablet
// (768-1024px) and hide BottomTabBar there — the badge-source code
// already lived here, so adding the second consumer is cheap.
function MobileShell() {
  const bp = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const isPhone = bp === 'phone';
  const isTablet = bp === 'tablet';
  // Both phone + tablet share the badge plumbing and MoreSheet; only
  // the visible nav surface differs (BottomTabBar vs. AppSidebar).
  const isMobileOrTablet = isPhone || isTablet;
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  // Alerts unread count — same hook the top navbar uses. The hook is
  // identity-aware (authKey) and degrades to polling, so mounting a
  // second consumer is safe.
  const alertsCount = useAlertsWebSocket(isMobileOrTablet && !isAuthPage, user?.id ?? null);

  // Bookmark count: cheap GET on mount + a window event so individual
  // pages that add/remove bookmarks can ask us to refresh without
  // pulling in a state-management lib. Bookmark API requires auth; if
  // there's no user yet, hold the count at 0 and skip the call.
  const refreshSavedCount = useCallback(() => {
    if (!user) {
      setSavedCount(0);
      return;
    }
    listBookmarks()
      .then((data) => {
        const arr = Array.isArray(data) ? data : data?.bookmarks || [];
        setSavedCount(arr.length);
      })
      .catch(() => { /* silent — badge defaults to 0 */ });
  }, [user]);

  useEffect(() => {
    if (!isMobileOrTablet) return undefined;
    refreshSavedCount();
    const onBookmarkChange = () => refreshSavedCount();
    window.addEventListener('bookmarks:changed', onBookmarkChange);
    return () => window.removeEventListener('bookmarks:changed', onBookmarkChange);
  }, [isMobileOrTablet, refreshSavedCount]);

  // Auto-close the More sheet whenever the URL changes (route was
  // pushed by tapping a sheet item). Belt-and-suspenders for the
  // explicit onClose passed to each item.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  if (!isMobileOrTablet) return null;
  // Hide the bar/sheet on auth pages — they're full-screen flows
  // where the nav rail/bar would be a distraction (and matches the
  // navbar's existing isAuthPage suppression of the alerts WS).
  if (isAuthPage) return null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {isPhone && (
        <BottomTabBar
          savedCount={savedCount}
          alertsCount={alertsCount}
          onMoreClick={() => setMoreOpen(true)}
        />
      )}
      {isTablet && (
        <AppSidebar
          savedCount={savedCount}
          alertsCount={alertsCount}
          onMoreClick={() => setMoreOpen(true)}
        />
      )}
      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onLogout={handleLogout}
        isAuthenticated={!!user}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AuthRedirectHandler />
        <div className="app-container">
          {/* Mounted once: saves scrollY on every scroll keyed by
              pathname+search, and restores it on browser back/forward.
              No-op on PUSH navigations (link clicks). See issue #126. */}
          <ScrollRestoration />
          <Navbar />
          {/* Phone-only BottomTabBar + MoreSheet (#704 PR-2). Returns
              null on tablet/desktop so the layout is unchanged there. */}
          <MobileShell />
          {/* Global compare-pin bar (#147 slice 2) — sticky at bottom
              of viewport when ≥1 parcel pinned; takes zero layout
              space when empty so existing page heights are unaffected. */}
          <ComparePinBar />
          <main className="main-content">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public routes — reachable without a session so a
                    signed-out user can actually authenticate (#280). */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                {/* Everything else requires login. RequireAuth is a
                    pathless layout route: anonymous visitors are
                    redirected to /login (with the attempted location
                    preserved); authenticated visitors get the child
                    route via <Outlet/>. */}
                <Route element={<RequireAuth />}>
                  <Route path="/" element={<SearchPage />} />
                  <Route path="/parcel/:parcelId" element={<ParcelDetail />} />
                  {/* On-demand deep-research streaming view (#1059) —
                      opened in a new tab by the "Deep research" button
                      and from completion alerts / the library. */}
                  <Route path="/parcel/:parcelId/research" element={<ParcelResearch />} />
                  {/* One-page printable parcel summary (#1171) — tight
                      single-sheet layout opened from the detail page's
                      print icon; distinct from /report/:parcelId. */}
                  <Route path="/parcel/:parcelId/print" element={<ParcelPrint />} />
                  <Route path="/research-library" element={<ResearchLibrary />} />
                  <Route path="/saved" element={<SavedParcels />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/admin/model-eval" element={<ModelEvalDashboard />} />
                  <Route path="/opportunities" element={<Opportunities />} />
                  <Route path="/properties" element={<Properties />} />
                  <Route path="/screens/big-cheap-acreage" element={<BigCheapAcreage />} />
                  <Route path="/screens/stale-acreage" element={<StaleAcreage />} />
                  <Route path="/screens/mineral-adjacent" element={<MineralAdjacentScreen />} />
                  <Route path="/screens/buildable-retreat" element={<BuildableRetreatScreen />} />
                  <Route path="/compare" element={<Compare />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/report/:parcelId" element={<Report />} />
                  <Route path="/watchlists" element={<Watchlists />} />
                  <Route path="/digest" element={<Digest />} />
                  <Route path="/notifications" element={<NotificationSettings />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </main>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
